import React, { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Pill, Button, Card } from "../../components/common/Primitives";
import AdminListSearch from "../../components/common/AdminListSearch";
import api, { formatApiError } from "../../lib/api";

export default function AdminQueries() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const load = () => api.get("/admin/queries").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [r.query_id, r.investor_id, r.startup_id, r.status, r.answer,
                   ...(r.questions || []).map((qq) => qq.question)].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  const decide = async (q, status) => {
    try {
      await api.post(`/admin/queries/${q.query_id}/decision`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell title="Queries" subtitle="ADMIN · MODERATE RESPONSES">
      <Toaster richColors position="top-right" />
      <AdminListSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by investor, startup, status, question text…"
        testId="admin-queries-search"
        count={filtered.length}
      />
      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/15 p-10 text-center text-ink-muted">
          {rows.length === 0 ? "No queries yet." : "No queries match your search."}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((q) => (
            <Card key={q.query_id} testId={`aq-${q.query_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="overline">Query · {q.query_id.slice(0, 12)}</div>
                <Pill tone={q.status === "Response Shared" ? "success" : q.status === "Startup Response Pending" ? "warn" : "muted"}>{q.status}</Pill>
              </div>
              <div className="text-ink-soft text-xs mb-3 font-mono">Investor: {q.investor_id} · Startup: {q.startup_id}</div>
              <ul className="space-y-2 mb-3">
                {(q.questions || []).map((qq) => <li key={qq.question_id} className="text-ink text-sm">• {qq.question}</li>)}
              </ul>
              {q.answer && (
                <div className="border border-white/10 p-3 mb-3 bg-black/30">
                  <div className="overline mb-1">Startup Response</div>
                  <div className="text-sm text-ink-soft leading-relaxed">{q.answer}</div>
                </div>
              )}
              {q.status === "Response Under Review" && (
                <div className="flex gap-2">
                  <Button variant="dark" onClick={() => decide(q, "Response Shared")} data-testid={`share-${q.query_id}`}>Share with Investor</Button>
                  <Button variant="dark" onClick={() => decide(q, "On Hold")} data-testid={`hold-${q.query_id}`}>Hold</Button>
                  <Button variant="dark" onClick={() => decide(q, "Rejected")} data-testid={`reject-${q.query_id}`}>Reject</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
